import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, ReferenceLine, ReferenceArea } from "recharts";
import { Download, Filter, X } from "lucide-react";
import { MealImpact } from "@/hooks/useExperimentResults";
import { useExperimentParticipants } from "@/hooks/useExperimentParticipants";
import { downloadGlucoseDataCSV, downloadCSV } from "@/lib/csvDownload";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import MiniGlucoseChart from "@/components/MiniGlucoseChart";
import { validateExperiment } from "@/lib/experimentValidation";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
interface ExperimentResultsListProps {
  results: MealImpact[];
  linkedProducts?: Array<{
    id: string;
    name: string;
    image_url?: string;
  }>;
  experimentId?: string;
  experimentName?: string;
}
const ProductImages: React.FC<{
  foodLogNames: string[];
  linkedProducts?: Array<{
    id: string;
    name: string;
    image_url?: string;
  }>;
}> = ({ foodLogNames, linkedProducts }) => {
  // Sort food log names alphabetically for consistent ordering
  const sortedFoodLogNames = [...foodLogNames].sort((a, b) => a.localeCompare(b));
  if (!linkedProducts || linkedProducts.length === 0) {
    return (
      <Tooltip delayDuration={50}>
        <TooltipTrigger asChild>
          <div className="flex flex-col gap-1 cursor-help">
            {sortedFoodLogNames.slice(0, 2).map((name, index) => (
              <div key={index} className="text-xs text-gray-600 truncate max-w-32">
                {name}
              </div>
            ))}
            {sortedFoodLogNames.length > 2 && (
              <div className="text-xs text-gray-400">+{sortedFoodLogNames.length - 2} more</div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="text-xs">
            <div className="font-medium mb-1">All products:</div>
            {sortedFoodLogNames.map((name, index) => (
              <div key={index}>{name}</div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Try to match food log names with linked products
  const matchedProducts = sortedFoodLogNames
    .map((name) =>
      linkedProducts.find(
        (product) =>
          product.name.toLowerCase().includes(name.toLowerCase()) ||
          name.toLowerCase().includes(product.name.toLowerCase()),
      ),
    )
    .filter(Boolean);
  return (
    <Tooltip delayDuration={50}>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-2 cursor-help">
          {matchedProducts.slice(0, 3).map((product, index) => (
            <div key={index} className="flex items-center gap-1">
              {product?.image_url && (
                <img src={product.image_url} alt={product.name} className="w-6 h-6 rounded object-cover" />
              )}
              <span className="text-xs text-gray-600 truncate max-w-24">{product?.name}</span>
            </div>
          ))}
          {sortedFoodLogNames.length > matchedProducts.length && (
            <div className="text-xs text-gray-400">+{sortedFoodLogNames.length - matchedProducts.length} more</div>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="text-xs">
          <div className="font-medium mb-1">All products:</div>
          {sortedFoodLogNames.map((name, index) => (
            <div key={index}>{name}</div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
const ExperimentResultsList: React.FC<ExperimentResultsListProps> = ({
  results,
  linkedProducts,
  experimentId,
  experimentName,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Filter state
  const [filters, setFilters] = useState({
    participant: "",
    testMeal: "",
    glucoseStartMin: "",
    glucoseStartMax: "",
    glucosePeakMin: "",
    glucosePeakMax: "",
    timeToPeakMin: "",
    timeToPeakMax: "",
    timeToReturn: "",
    satietyImpact: "",
    glucoseIaucMin: "",
    glucoseIaucMax: "",
    glycemicScoreMin: "",
    glycemicScoreMax: "",
    glycemicRange: "",
    valid: "",
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const hasActiveFilters = Object.values(filters).some((v) => v !== "");

  const clearFilters = () => {
    setFilters({
      participant: "",
      testMeal: "",
      glucoseStartMin: "",
      glucoseStartMax: "",
      glucosePeakMin: "",
      glucosePeakMax: "",
      timeToPeakMin: "",
      timeToPeakMax: "",
      timeToReturn: "",
      satietyImpact: "",
      glucoseIaucMin: "",
      glucoseIaucMax: "",
      glycemicScoreMin: "",
      glycemicScoreMax: "",
      glycemicRange: "",
      valid: "",
    });
  };

  const getFromLabel = (path: string) => {
    if (path.startsWith("/dashboard/experiments")) return "experiment details";
    if (path.startsWith("/dashboard/tastermonials")) return "tastermonials";
    if (path === "/dashboard") return "dashboard";
    if (path.startsWith("/dashboard/products")) return "products";
    if (path.startsWith("/dashboard/participants")) return "participants";
    return "previous page";
  };
  // Participants for completed_at mapping
  const { data: participants = [] } = useExperimentParticipants(experimentId || "");
  const participantsByUserExperimentId = useMemo(() => {
    const m = new Map<string, unknown>();
    participants.forEach((p) => m.set(p.userExperimentId, p));
    return m;
  }, [participants]);
  const participantsByUserId = useMemo(() => {
    const m = new Map<string, unknown>();
    participants.forEach((p) => m.set(p.userId, p));
    return m;
  }, [participants]);
  const getResultCompletedAt = (r: MealImpact) => r.experiment_timestamp_local;
  // Group and sort results by participant
  const resultsWithParticipants = useMemo(() => {
    // Create a map of unique participant IDs
    const participantMap = new Map<string, number>();
    let participantCounter = 1;

    // Sort results by user_id first, then by timestamp
    const sortedResults = [...results].sort((a, b) => {
      // First sort by user_id
      if (a.user_id !== b.user_id) {
        return a.user_id.localeCompare(b.user_id);
      }
      // Then by timestamp within same participant
      return new Date(a.adjusted_meal_timestamp_utc).getTime() - new Date(b.adjusted_meal_timestamp_utc).getTime();
    });

    // Assign participant numbers
    return sortedResults.map((result) => {
      if (!participantMap.has(result.user_id)) {
        participantMap.set(result.user_id, participantCounter++);
      }
      return {
        ...result,
        participantNumber: participantMap.get(result.user_id)!,
      };
    });
  }, [results]);

  // Apply filters
  const filteredResults = useMemo(() => {
    return resultsWithParticipants.filter((result) => {
      const participant = participantsByUserId.get(result.user_id) as { tags?: string[] } | undefined;
      const isDiabetic = participant?.tags?.includes("Diabetic") || false;
      const validation = validateExperiment(result, isDiabetic);

      // Participant filter
      if (filters.participant && !result.participantNumber.toString().includes(filters.participant)) {
        return false;
      }

      // Test Meal filter
      if (filters.testMeal) {
        const mealMatch = result.food_log_names.some((name) =>
          name.toLowerCase().includes(filters.testMeal.toLowerCase())
        );
        if (!mealMatch) return false;
      }

      // Glucose Start range
      if (filters.glucoseStartMin && (result.start_glucose_level == null || result.start_glucose_level < parseFloat(filters.glucoseStartMin))) {
        return false;
      }
      if (filters.glucoseStartMax && (result.start_glucose_level == null || result.start_glucose_level > parseFloat(filters.glucoseStartMax))) {
        return false;
      }

      // Glucose Peak range
      if (filters.glucosePeakMin && (result.peak_glucose_level == null || result.peak_glucose_level < parseFloat(filters.glucosePeakMin))) {
        return false;
      }
      if (filters.glucosePeakMax && (result.peak_glucose_level == null || result.peak_glucose_level > parseFloat(filters.glucosePeakMax))) {
        return false;
      }

      // Time to Peak range
      if (filters.timeToPeakMin && (result.time_to_peak == null || result.time_to_peak < parseFloat(filters.timeToPeakMin))) {
        return false;
      }
      if (filters.timeToPeakMax && (result.time_to_peak == null || result.time_to_peak > parseFloat(filters.timeToPeakMax))) {
        return false;
      }

      // Time to Return filter
      if (filters.timeToReturn) {
        const returnMinutes = result.time_to_return != null ? new Date(result.time_to_return).getMinutes() : null;
        if (returnMinutes == null || returnMinutes.toString() !== filters.timeToReturn) return false;
      }

      // Satiety Impact filter
      if (filters.satietyImpact && filters.satietyImpact !== "all") {
        if (result.satiety_score == null || result.satiety_score.toString() !== filters.satietyImpact) return false;
      }

      // Glucose iAUC range
      if (filters.glucoseIaucMin && (result.iauc == null || result.iauc < parseFloat(filters.glucoseIaucMin))) {
        return false;
      }
      if (filters.glucoseIaucMax && (result.iauc == null || result.iauc > parseFloat(filters.glucoseIaucMax))) {
        return false;
      }

      // Glycemic Impact Score range
      if (filters.glycemicScoreMin) {
        const score = result.indicative_glycemic != null ? result.indicative_glycemic * 100 : null;
        if (score == null || score < parseFloat(filters.glycemicScoreMin)) return false;
      }
      if (filters.glycemicScoreMax) {
        const score = result.indicative_glycemic != null ? result.indicative_glycemic * 100 : null;
        if (score == null || score > parseFloat(filters.glycemicScoreMax)) return false;
      }

      // Glycemic Range filter
      if (filters.glycemicRange && filters.glycemicRange !== "all") {
        if (result.glycemic_range !== filters.glycemicRange) return false;
      }

      // Valid filter
      if (filters.valid && filters.valid !== "all") {
        const isValid = validation.isValid;
        if (filters.valid === "valid" && !isValid) return false;
        if (filters.valid === "invalid" && isValid) return false;
      }

      return true;
    });
  }, [resultsWithParticipants, filters, participantsByUserId]);


  // Define a palette of 16 colors for participants
  const participantColors = [
    "bg-blue-500 text-white",
    "bg-green-500 text-white",
    "bg-purple-500 text-white",
    "bg-red-500 text-white",
    "bg-yellow-500 text-black",
    "bg-pink-500 text-white",
    "bg-indigo-500 text-white",
    "bg-orange-500 text-white",
    "bg-teal-500 text-white",
    "bg-cyan-500 text-white",
    "bg-lime-500 text-black",
    "bg-rose-500 text-white",
    "bg-violet-500 text-white",
    "bg-emerald-500 text-white",
    "bg-amber-500 text-black",
    "bg-slate-500 text-white",
  ];
  const getParticipantColor = (participantNumber: number) => {
    return participantColors[(participantNumber - 1) % participantColors.length];
  };
  const getGlycemicBadgeColor = (glycemic: number | string) => {
    // Convert number to string representation
    let glycemicStr = "";
    if (typeof glycemic === "number") {
      // Map numeric values to categories (adjust ranges as needed)
      if (glycemic <= 0.3) glycemicStr = "low";
      else if (glycemic <= 0.7) glycemicStr = "medium";
      else glycemicStr = "high";
    } else {
      glycemicStr = glycemic?.toLowerCase() || "";
    }
    switch (glycemicStr) {
      case "low":
        return "bg-green-100 text-green-800 border-green-300";
      case "medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "high":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };
  const formatTimestamp = (timestamp: string, timezone?: string) => {
    const date = new Date(timestamp);
    let timeZone = "UTC"; // Default fallback

    // Handle timezone - if it's a number like "-7", convert or fallback to UTC
    if (timezone) {
      // Check if timezone is a valid IANA timezone identifier
      try {
        // Test if the timezone is valid by trying to use it
        new Intl.DateTimeFormat("en", {
          timeZone: timezone,
        }).format(date);
        timeZone = timezone;
      } catch (error) {
        // If timezone is invalid (like "-7"), fallback to UTC
        timeZone = "UTC";
      }
    }
    const dateStr = date.toLocaleDateString(undefined, {
      timeZone,
    });
    const timeStr = date
      .toLocaleTimeString(undefined, {
        timeZone,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/:/g, ".")
      .replace(/\s+(AM|PM)/i, "$1");
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">
            <div className="text-sm font-medium">{timeStr}</div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm max-w-xs">
            The time of the meal is shown in the participants local time to indicate relation to circadian rhythm
          </p>
        </TooltipContent>
      </Tooltip>
    );
  };
  const sanitizeFilename = (name?: string) =>
    name ? name.replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "") : "experiment_results";
  const handleDownloadCombinedCSV = () => {
    const headers = [
      "participant_number",
      "tier",
      "age_group",
      "gender",
      "ethnicity",
      "bmi_group",
      "tags",
      "completed_at",
      "meal_timestamp_utc",
      "timezone",
      "food_log_names",
      "start_glucose_level",
      "peak_glucose_level",
      "time_to_peak",
      "time_to_return",
      "iauc",
      "indicative_glycemic",
      "glycemic_range",
      "experiment_valid",
      "satiety_score",
      "Glucose -30m to +120m",
      "Baseline -30m to +120m",
    ];
    const toSeriesString = (samples?: number[][]) => {
      if (!samples || samples.length === 0) return "";
      // Determine starting glucose as the value closest to time 0
      const startingSampleOffset = Math.min(...samples.map((s) => Math.abs(s[0])));
      const startingGlucose = samples.find((s) => Math.abs(s[0]) === startingSampleOffset)?.[1] ?? 0;
      const minSec = -30 * 60;
      const maxSec = 120 * 60;
      return samples
        .filter((s) => s[0] >= minSec && s[0] <= maxSec)
        .map((s) => {
          const minutes = s[0] / 60;
          const minutesFormatted = Number.isInteger(minutes) ? minutes : parseFloat(minutes.toFixed(1));
          const delta = parseFloat((s[1] - startingGlucose).toFixed(2));
          return `${minutesFormatted}:${delta}`;
        })
        .join("; ");
    };
    const rows = resultsWithParticipants.map(
      (
        r: MealImpact & {
          participantNumber?: number;
        },
      ) => {
        const p = participantsByUserId.get(r.user_id) as
          | {
              tier?: string;
              ageGroup?: string;
              gender?: string;
              ethnicity?: string;
              bmiGroup?: string;
              tags?: string[];
              completedAt?: string;
            }
          | undefined;
        const isDiabetic = p?.tags?.includes("Diabetic") || false;
        const validation = validateExperiment(r, isDiabetic);
        const glucoseSeries = toSeriesString(r.glucose_samples);
        const baselineSeries = toSeriesString(r.baseline_glucose_samples);
        return [
          r.participantNumber ?? "",
          p?.tier ?? "",
          p?.ageGroup ?? "",
          p?.gender ?? "",
          p?.ethnicity ?? "",
          p?.bmiGroup ?? "",
          Array.isArray(p?.tags) ? p.tags.join("; ") : "",
          p?.completedAt ?? "",
          r.meal_timestamp_utc ?? "",
          r.timezone ?? "",
          Array.isArray(r.food_log_names) ? r.food_log_names.join("; ") : "",
          r.start_glucose_level ?? "",
          r.peak_glucose_level ?? "",
          r.time_to_peak ?? "",
          r.time_to_return ?? "",
          r.iauc ?? "",
          r.indicative_glycemic ?? "",
          r.glycemic_range ?? "",
          validation.isValid ? "true" : "false",
          r.satiety_score ?? "",
          glucoseSeries,
          baselineSeries,
        ];
      },
    );
    const base = sanitizeFilename(experimentName);
    downloadCSV({
      data: rows,
      headers,
      filename: `${base}.csv`,
    });
  };
  return (
    <TooltipProvider>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Individual Experiment Results</CardTitle>
            <CardDescription>
              Logged meal impacts and glucose responses ({filteredResults.length}{hasActiveFilters ? ` of ${results.length}` : ""} results)
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadCombinedCSV} title="Download combined results CSV">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
            <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={hasActiveFilters ? "border-primary" : ""}>
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                  {hasActiveFilters && (
                    <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 flex items-center justify-center">
                      {Object.values(filters).filter((v) => v !== "").length}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 max-h-[500px] overflow-y-auto" align="end">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Filter Results</h4>
                  {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2">
                      <X className="h-4 w-4 mr-1" />
                      Clear all
                    </Button>
                  )}
                </div>
                <div className="space-y-4">
                  {/* Participant */}
                  <div className="space-y-2">
                    <Label htmlFor="filter-participant">Participant #</Label>
                    <Input
                      id="filter-participant"
                      placeholder="e.g. 1, 2, 3..."
                      value={filters.participant}
                      onChange={(e) => setFilters((f) => ({ ...f, participant: e.target.value }))}
                    />
                  </div>

                  {/* Test Meal */}
                  <div className="space-y-2">
                    <Label htmlFor="filter-meal">Test Meal</Label>
                    <Input
                      id="filter-meal"
                      placeholder="Search meal name..."
                      value={filters.testMeal}
                      onChange={(e) => setFilters((f) => ({ ...f, testMeal: e.target.value }))}
                    />
                  </div>

                  {/* Glucose Start */}
                  <div className="space-y-2">
                    <Label>Glucose Start (mg/dL)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={filters.glucoseStartMin}
                        onChange={(e) => setFilters((f) => ({ ...f, glucoseStartMin: e.target.value }))}
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={filters.glucoseStartMax}
                        onChange={(e) => setFilters((f) => ({ ...f, glucoseStartMax: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Glucose Peak */}
                  <div className="space-y-2">
                    <Label>Glucose Peak (mg/dL)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={filters.glucosePeakMin}
                        onChange={(e) => setFilters((f) => ({ ...f, glucosePeakMin: e.target.value }))}
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={filters.glucosePeakMax}
                        onChange={(e) => setFilters((f) => ({ ...f, glucosePeakMax: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Time to Peak */}
                  <div className="space-y-2">
                    <Label>Time To Peak (mins)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={filters.timeToPeakMin}
                        onChange={(e) => setFilters((f) => ({ ...f, timeToPeakMin: e.target.value }))}
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={filters.timeToPeakMax}
                        onChange={(e) => setFilters((f) => ({ ...f, timeToPeakMax: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Time to Return */}
                  <div className="space-y-2">
                    <Label htmlFor="filter-return">Time to Return (mins)</Label>
                    <Input
                      id="filter-return"
                      type="number"
                      placeholder="Enter minutes..."
                      value={filters.timeToReturn}
                      onChange={(e) => setFilters((f) => ({ ...f, timeToReturn: e.target.value }))}
                    />
                  </div>

                  {/* Satiety Impact */}
                  <div className="space-y-2">
                    <Label>Satiety Impact</Label>
                    <Select
                      value={filters.satietyImpact}
                      onValueChange={(value) => setFilters((f) => ({ ...f, satietyImpact: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="1">1 - Very hungry</SelectItem>
                        <SelectItem value="2">2 - Hungry</SelectItem>
                        <SelectItem value="3">3 - Neutral</SelectItem>
                        <SelectItem value="4">4 - Satisfied</SelectItem>
                        <SelectItem value="5">5 - Very full</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Glucose iAUC */}
                  <div className="space-y-2">
                    <Label>Glucose iAUC</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={filters.glucoseIaucMin}
                        onChange={(e) => setFilters((f) => ({ ...f, glucoseIaucMin: e.target.value }))}
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={filters.glucoseIaucMax}
                        onChange={(e) => setFilters((f) => ({ ...f, glucoseIaucMax: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Glycemic Impact Score */}
                  <div className="space-y-2">
                    <Label>Glycemic Impact Score</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={filters.glycemicScoreMin}
                        onChange={(e) => setFilters((f) => ({ ...f, glycemicScoreMin: e.target.value }))}
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={filters.glycemicScoreMax}
                        onChange={(e) => setFilters((f) => ({ ...f, glycemicScoreMax: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Glycemic Range */}
                  <div className="space-y-2">
                    <Label>Glycemic Range</Label>
                    <Select
                      value={filters.glycemicRange}
                      onValueChange={(value) => setFilters((f) => ({ ...f, glycemicRange: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Moderate">Moderate</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Valid */}
                  <div className="space-y-2">
                    <Label>Valid</Label>
                    <Select
                      value={filters.valid}
                      onValueChange={(value) => setFilters((f) => ({ ...f, valid: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="valid">Valid</SelectItem>
                        <SelectItem value="invalid">Invalid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </CardHeader>
        <CardContent>
          {filteredResults.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              {hasActiveFilters ? "No results match your filters." : "No experiment results available yet."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Participant</TableHead>
                  <TableHead>Test Meal</TableHead>
                  <TableHead>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">Local time</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-sm max-w-xs">
                          The time of the meal is shown in the participants local time to indicate relation to circadian
                          rhythm
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead>Chart</TableHead>
                  <TableHead>Glucose Start (mg/dL)</TableHead>
                  <TableHead>Glucose Peak (mg/dL)</TableHead>
                  <TableHead>Time To Peak (mins)</TableHead>
                  <TableHead>Time to Return</TableHead>
                  <TableHead>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">Satiety Impact</span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-md">
                        <p className="text-sm mb-2">
                          Satiety Impact measures how well a food keeps you <strong>feeling full after eating</strong>. A higher score means longer-lasting fullness and less likelihood of snacking soon after.
                        </p>
                        <p className="text-sm font-semibold mb-1">Why it matters</p>
                        <p className="text-sm mb-2">
                          Foods with a <strong>high glycemic impact (bad for glucose control)</strong> often lead to <strong>lower satiety</strong>, leaving you hungry sooner. By comparing Satiety Impact scores, you can see which foods not only keep blood sugar steadier but also <strong>help control appetite</strong>.
                        </p>
                        <p className="text-sm">
                          👉 <em>Rule of thumb</em>: <strong>Higher Satiety Impact = better for fullness and healthier choices.</strong>
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">Glucose iAUC</span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-md">
                        <p className="text-sm mb-2">
                          Incremental Area Under the Curve (iAUC) <strong>measures the total blood sugar rise above baseline</strong> after eating, showing not just the spike but <strong>how long levels stay elevated</strong>. This gives a clearer picture of a food's <strong>overall glucose impact</strong> than peak values alone.
                        </p>
                        <p className="text-sm">
                          A quick rise that falls fast is less concerning than a long, drawn-out elevation. People with lower insulin sensitivity often show higher iAUC because their bodies take longer to clear sugar. Elevated glucose over time can stress blood vessels and metabolism, so lowering iAUC is a meaningful health benefit.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">Glycemic Impact Score</span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-md">
                        <p className="text-sm font-semibold mb-2">Tastermonial Glycemic Impact Score (GI Score)</p>
                        <p className="text-sm mb-2">
                          The GI score measures an <strong>individual's 2-hour glucose response</strong> to a test meal, normalized to their own response to a 50 g glucose load.
                        </p>
                        <p className="text-sm">
                          This makes it possible to compare the sugar impact of different meals or products within the same category, accounting for personal metabolic differences.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead>Glycemic Range</TableHead>
                  <TableHead>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">Valid</span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-md">
                        <p className="text-sm font-semibold mb-2">Valid Experiment</p>
                        <p className="text-sm mb-2">
                          The glucose level before the test meal must be in the normal range for the result to be valid:
                        </p>
                        <ul className="text-sm list-disc list-inside mb-2 space-y-1">
                          <li><strong>Non-diabetic users</strong>: 70–110 mg/dL</li>
                          <li><strong>Diabetic users</strong>: 70–130 mg/dL</li>
                        </ul>
                        <p className="text-sm">
                          <strong>Why:</strong> Starting from a stable baseline ensures the glucose rise is due to the test meal itself, not influenced by a prior spike, dip, or uncontrolled variability. This makes the results more accurate and comparable across users.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredResults.map((result) => (
                  <TableRow
                    key={result.meal_id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate(`/dashboard/participants/${result.user_id}`, {
                        state: {
                          from: location.pathname,
                          fromLabel: getFromLabel(location.pathname),
                        },
                      })
                    }
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${getParticipantColor(result.participantNumber)}`}
                        >
                          #{result.participantNumber}
                        </div>
                        {/* Show diabetic label only if participant has diabetic tag */}
                        {(
                          participantsByUserId.get(result.user_id) as
                            | {
                                tags?: string[];
                              }
                            | undefined
                        )?.tags?.includes("Diabetic") && (
                          <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300 text-xs">
                            Diabetic
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <ProductImages foodLogNames={result.food_log_names} linkedProducts={linkedProducts} />
                    </TableCell>
                    <TableCell>
                      {new Date(getResultCompletedAt(result))
                        .toLocaleTimeString(undefined, {
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })
                        .replace(/:/g, ".")
                        .replace(/\s+(AM|PM)/i, "$1")}
                    </TableCell>
                    <TableCell>
                      <MiniGlucoseChart
                        samples={result.glucose_samples}
                        startGlucose={result.start_glucose_level}
                        baselineSamples={result.baseline_glucose_samples}
                      />
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {result.start_glucose_level != null ? `${result.start_glucose_level}` : "N/A"}
                    </TableCell>
                    <TableCell className="text-sm font-medium">{result.peak_glucose_level}</TableCell>
                    <TableCell className="text-sm font-medium">
                      {result.time_to_peak != null ? result.time_to_peak : "N/A"}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {result.time_to_return != null ? new Date(result.time_to_return).getMinutes() : "N/A"}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {result.satiety_score ? (
                        <>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            {result.satiety_score}/5
                          </Badge>
                          <div className="text-xs text-gray-500 mt-1">
                            {result.satiety_score === 1
                              ? "Very hungry"
                              : result.satiety_score === 2
                                ? "Hungry"
                                : result.satiety_score === 3
                                  ? "Neutral"
                                  : result.satiety_score === 4
                                    ? "Satisfied"
                                    : result.satiety_score === 5
                                      ? "Very full"
                                      : "Unknown"}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {result.iauc !== null && result.iauc !== undefined ? (
                        result.iauc.toFixed(1)
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // Show N/A if glycemic is 0.0 and experiment is invalid or missing start data
                        if (
                          result.indicative_glycemic === 0.0 &&
                          (!result.experiment_valid || result.start_glucose_level == null)
                        ) {
                          return <span className="text-sm font-medium">N/A</span>;
                        }
                        return (
                          <Badge variant="outline" className={getGlycemicBadgeColor(result.indicative_glycemic)}>
                            {typeof result.indicative_glycemic === "number"
                              ? (result.indicative_glycemic * 100).toFixed(0)
                              : result.indicative_glycemic || "Unknown"}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {result.glycemic_range != null ? result.glycemic_range : "N/A"}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const participant = participantsByUserId.get(result.user_id) as
                          | {
                              tags?: string[];
                            }
                          | undefined;
                        const isDiabetic = participant?.tags?.includes("Diabetic") || false;
                        const validation = validateExperiment(result, isDiabetic);
                        if (validation.isValid) {
                          return <Badge variant="default">Valid</Badge>;
                        } else {
                          return (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div onClick={(e) => e.stopPropagation()}>
                                  <Badge variant="secondary" className="cursor-help">
                                    Invalid
                                  </Badge>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-sm max-w-xs">{validation.reason}</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        }
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};
export default ExperimentResultsList;
